// components/ArticlesComparator.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiFilter,
  FiDownload,
  FiRefreshCw,
  FiDatabase,
  FiClock,
  FiWifi,
  FiWifiOff,
  FiMenu,
  FiX,
  FiArrowUp,
  FiArrowDown,
  FiChevronDown,
  FiTrendingUp,
  FiEye,
  FiCheckSquare,
  FiSquare,
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import Dexie from 'dexie';
import API_CONFIG from '../components/API_CONFIG/API_CONFIG';

// ==================== BASE DE DONNÉES & UTILITAIRES ====================
const db = new Dexie('ArticlesTableCache');
db.version(1).stores({
  articles: 'id, company_name, description, unit_price, cacheId',
  companies: 'name',
  cacheInfo: 'key',
  requestLog: '++id, timestamp, endpoint',
});

class RateLimiter {
  constructor(maxRequests = 5, timeWindow = 60000) {
    this.maxRequests = maxRequests;
    this.timeWindow = timeWindow;
    this.requests = [];
    this.requestQueue = [];
    this.isProcessing = false;
  }

  async acquire() {
    return new Promise((resolve) => {
      this.requestQueue.push(resolve);
      this.processQueue();
    });
  }

  async processQueue() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    while (this.requestQueue.length > 0) {
      const now = Date.now();
      this.requests = this.requests.filter(time => now - time < this.timeWindow);

      if (this.requests.length < this.maxRequests) {
        const resolve = this.requestQueue.shift();
        this.requests.push(now);
        resolve();
      } else {
        const oldestRequest = this.requests[0];
        const waitTime = this.timeWindow - (now - oldestRequest);
        await new Promise(resolve => setTimeout(resolve, waitTime + 100));
      }
    }

    this.isProcessing = false;
  }
}

const fetchWithRetry = async (url, options = {}, retries = 3, delay = 1000) => {
  for (let i = 0; i < retries; i++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      if (i === retries - 1) throw error;
      await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
    }
  }
};

const rateLimiter = new RateLimiter(3, 60000);

const logRequest = async (endpoint) => {
  try {
    await db.requestLog.add({ endpoint, timestamp: new Date().toISOString() });
    const count = await db.requestLog.count();
    if (count > 100) {
      const oldest = await db.requestLog.orderBy('id').first();
      if (oldest) await db.requestLog.delete(oldest.id);
    }
  } catch (error) {
    console.error('Erreur logging:', error);
  }
};

const isCacheValid = async () => {
  try {
    const cacheInfo = await db.cacheInfo.get('articles_data');
    if (!cacheInfo) return false;
    const now = new Date();
    const cacheDate = new Date(cacheInfo.timestamp);
    const hoursDiff = (now - cacheDate) / (1000 * 60 * 60);
    return hoursDiff < 24;
  } catch (error) {
    console.error('Erreur cache:', error);
    return false;
  }
};

const saveToCache = async (articles, companies) => {
  try {
    await db.articles.clear();
    await db.companies.clear();
    const articlesWithId = articles.map((article, index) => ({ ...article, cacheId: index + 1 }));
    if (articlesWithId.length > 0) {
      const batchSize = 50;
      for (let i = 0; i < articlesWithId.length; i += batchSize) {
        const batch = articlesWithId.slice(i, i + batchSize);
        await db.articles.bulkAdd(batch);
      }
    }
    if (companies.length > 0) await db.companies.bulkAdd(companies.map(c => ({ name: c })));
    await db.cacheInfo.put({ key: 'articles_data', timestamp: new Date().toISOString() });
    console.log('Données sauvegardées dans le cache');
  } catch (error) {
    console.error('Erreur sauvegarde cache:', error);
  }
};

const loadFromCache = async () => {
  try {
    const articles = await db.articles.toArray();
    const companies = await db.companies.toArray();
    return {
      articles: articles.map(({ cacheId, ...rest }) => rest),
      companies: companies.map(c => c.name)
    };
  } catch (error) {
    console.error('Erreur chargement cache:', error);
    return { articles: [], companies: [] };
  }
};

const clearCache = async () => {
  try {
    await db.articles.clear();
    await db.companies.clear();
    await db.cacheInfo.clear();
    console.log('Cache vidé');
  } catch (error) {
    console.error('Erreur vidage cache:', error);
  }
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '0 FC';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'CDF' }).format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return 'Jamais';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Date invalide';
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(date);
};

// ==================== COMPOSANTS RÉUTILISABLES ====================
const Badge = ({ children, color = 'blue' }) => {
  const colors = {
    red: 'bg-red-500/10 border-red-500/20 text-red-400',
    green: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    blue: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
    purple: 'bg-purple-500/10 border-purple-500/20 text-purple-400',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${colors[color] || colors.blue}`}>
      {children}
    </span>
  );
};

const ArticleCardMobile = ({ group, expanded, onToggle, onSelect, selected, onViewDetails }) => (
  <motion.div layout className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-2xl p-4 mb-3">
    <div className="flex items-start gap-3 mb-3">
      <button onClick={() => onSelect(group.id)} className="mt-0.5">
        {selected ? <FiCheckSquare className="text-blue-400" size={20} /> : <FiSquare className="text-slate-500" size={20} />}
      </button>
      <div className="flex-1 min-w-0">
        <h3 className="text-white font-semibold text-sm truncate">{group.description}</h3>
        <p className="text-slate-400 text-xs">{group.company_name}</p>
      </div>
      <button onClick={() => onToggle(group.id)} className="p-1 text-slate-400">
        <motion.div animate={{ rotate: expanded ? 180 : 0 }}><FiChevronDown size={18} /></motion.div>
      </button>
    </div>

    <div className="grid grid-cols-2 gap-2 text-xs mb-3">
      <div className="bg-white/5 rounded-lg p-2 text-center">
        <div className="text-slate-400">Occ.</div>
        <div className="text-white font-bold">{group.count}</div>
      </div>
      <div className="bg-white/5 rounded-lg p-2 text-center">
        <div className="text-slate-400">Min</div>
        <div className="text-emerald-400 font-bold">{formatCurrency(group.minPrice)}</div>
      </div>
      <div className="bg-white/5 rounded-lg p-2 text-center">
        <div className="text-slate-400">Max</div>
        <div className="text-red-400 font-bold">{formatCurrency(group.maxPrice)}</div>
      </div>
      <div className="bg-white/5 rounded-lg p-2 text-center">
        <div className="text-slate-400">Moy.</div>
        <div className="text-blue-400 font-bold">{formatCurrency(group.averagePrice)}</div>
      </div>
    </div>

    <AnimatePresence>
      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="overflow-hidden"
        >
          <div className="pt-2 border-t border-white/10 space-y-2">
            {group.articles.map((article, idx) => (
              <div
                key={idx}
                onClick={() => onViewDetails(article)}
                className="flex justify-between items-center p-2 rounded-xl bg-white/5 hover:bg-white/10 cursor-pointer transition-colors"
              >
                <span className="text-white text-xs">{article.description}</span>
                <span className="text-emerald-400 font-mono text-xs">{formatCurrency(article.unit_price)}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

const MobileMenu = ({ isOpen, onClose, onRefresh, onClearCache, onExport, onCompare, compareCount, isRefreshing }) => (
  <AnimatePresence>
    {isOpen && (
      <>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={onClose} />
        <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 25 }} className="fixed right-0 top-0 h-full w-72 bg-gradient-to-b from-blue-950/95 to-slate-900/95 backdrop-blur-xl border-l border-blue-400/20 shadow-2xl z-50 p-6">
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-white font-bold text-lg">Menu</h3>
            <button onClick={onClose} className="p-2 rounded-xl bg-white/5 text-slate-400 hover:text-white"><FiX size={18} /></button>
          </div>
          <div className="space-y-4">
            <button onClick={onRefresh} disabled={isRefreshing} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-400 disabled:opacity-50">
              <FiRefreshCw className={isRefreshing ? 'animate-spin' : ''} /> {isRefreshing ? 'Mise à jour...' : 'Actualiser'}
            </button>
            <button onClick={onClearCache} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400">
              <FiDatabase /> Vider le cache
            </button>
            <button onClick={onExport} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-blue-500/10 border border-blue-400/20 text-blue-400">
              <FiDownload /> Exporter CSV
            </button>
            <button onClick={onCompare} disabled={compareCount === 0} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-purple-500/10 border border-purple-500/20 text-purple-400 disabled:opacity-50">
              <FiTrendingUp /> Comparer ({compareCount})
            </button>
          </div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

const MobileFilters = ({ isOpen, onClose, companies, selectedCompany, setSelectedCompany, minPrice, setMinPrice, maxPrice, setMaxPrice, onReset }) => {
  const [localMin, setLocalMin] = useState(minPrice);
  const [localMax, setLocalMax] = useState(maxPrice);
  const [localCompany, setLocalCompany] = useState(selectedCompany);

  const handleApply = () => {
    setMinPrice(localMin);
    setMaxPrice(localMax);
    setSelectedCompany(localCompany);
    onClose();
  };
  const handleReset = () => {
    setLocalMin(''); setLocalMax(''); setLocalCompany('');
    setMinPrice(''); setMaxPrice(''); setSelectedCompany('');
    onClose(); onReset();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={onClose} />
          <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', damping: 25 }} className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-blue-950/95 to-slate-900/95 backdrop-blur-xl border-t border-blue-400/20 rounded-t-2xl z-50 p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-white font-bold text-lg flex items-center gap-2"><FiFilter /> Filtres</h3>
              <button onClick={onClose} className="p-2 rounded-xl bg-white/5 text-slate-400"><FiX size={18} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-slate-400 text-xs uppercase block mb-2">Entreprise</label>
                <select value={localCompany} onChange={(e) => setLocalCompany(e.target.value)} className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white">
                  <option value="">Toutes</option>
                  {companies.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-slate-400 text-xs uppercase block mb-2">Prix min (CDF)</label>
                <input type="number" placeholder="0" value={localMin} onChange={(e) => setLocalMin(e.target.value)} className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white" />
              </div>
              <div>
                <label className="text-slate-400 text-xs uppercase block mb-2">Prix max (CDF)</label>
                <input type="number" placeholder="1 000 000" value={localMax} onChange={(e) => setLocalMax(e.target.value)} className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white" />
              </div>
              <div className="flex gap-3">
                <button onClick={handleApply} className="flex-1 py-3 bg-blue-500/20 border border-blue-400/30 text-blue-400 rounded-xl font-medium">Appliquer</button>
                <button onClick={handleReset} className="flex-1 py-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl font-medium">Réinitialiser</button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// ==================== COMPOSANT PRINCIPAL ====================
const ActiclesTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [cacheDate, setCacheDate] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('description');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [companies, setCompanies] = useState([]);

  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [comparisonResult, setComparisonResult] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});

  const lastFetchRef = useRef(null);
  const fetchTimeoutRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const debouncedFetch = useCallback((callback, delay = 1000) => {
    if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    fetchTimeoutRef.current = setTimeout(callback, delay);
  }, []);

  const fetchData = useCallback(async (forceRefresh = false) => {
    try {
      if (!forceRefresh && lastFetchRef.current && Date.now() - lastFetchRef.current < 10000) {
        const cached = await loadFromCache();
        if (cached && cached.articles.length) {
          setData(cached.articles);
          setCompanies(cached.companies);
          return;
        }
      }

      setLoading(true);
      setError(null);

      if (!forceRefresh) {
        const cacheValid = await isCacheValid();
        if (cacheValid) {
          const cached = await loadFromCache();
          if (cached && cached.articles.length) {
            setData(cached.articles);
            setCompanies(cached.companies);
            const cacheInfo = await db.cacheInfo.get('articles_data');
            if (cacheInfo) setCacheDate(cacheInfo.timestamp);
            if (isOnline) debouncedFetch(() => refreshDataInBackground(), 15000);
            setLoading(false);
            return;
          }
        }
      }

      await rateLimiter.acquire();
      const response = await fetchWithRetry(`${API_CONFIG.apiUrl}articles.php`);
      const text = await response.text();
      if (!text || text.trim() === '') throw new Error('Réponse vide');
      const result = JSON.parse(text);
      if (result.success) {
        setData(result.data);
        const uniqueCompanies = [...new Set(result.data.map(item => item.company_name))];
        setCompanies(uniqueCompanies);
        await saveToCache(result.data, uniqueCompanies);
        lastFetchRef.current = Date.now();
        const cacheInfo = await db.cacheInfo.get('articles_data');
        if (cacheInfo) setCacheDate(cacheInfo.timestamp);
        setSuccess(`✅ ${result.data.length} articles chargés`);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error(result.message || 'Erreur serveur');
      }
    } catch (err) {
      if (err.message?.includes('429') || err.status === 429) {
        setError('Trop de requêtes. Patientez...');
        setTimeout(() => fetchData(false), 30000);
      } else {
        const cached = await loadFromCache();
        if (cached && cached.articles.length) {
          setData(cached.articles);
          setCompanies(cached.companies);
          setError(null);
        } else {
          setError('Impossible de charger les données.');
        }
      }
    } finally {
      setLoading(false);
    }
  }, [isOnline, debouncedFetch]);

  const refreshDataInBackground = async () => {
    try {
      await rateLimiter.acquire();
      const response = await fetchWithRetry(`${API_CONFIG.apiUrl}articles.php`);
      const result = await response.json();
      if (result.success) {
        setData(result.data);
        const uniqueCompanies = [...new Set(result.data.map(item => item.company_name))];
        setCompanies(uniqueCompanies);
        await saveToCache(result.data, uniqueCompanies);
        lastFetchRef.current = Date.now();
      }
    } catch (err) { /* silent */ }
  };

  const manualRefresh = async () => {
    if (!isOnline) {
      setError('Pas de connexion internet.');
      setTimeout(() => setError(null), 3000);
      return;
    }
    setIsRefreshing(true);
    setError(null);
    try {
      await fetchData(true);
    } catch (err) {
      setError('Échec du rafraîchissement');
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsRefreshing(false);
    }
  };

  const clearCacheAndReload = async () => {
    await clearCache();
    lastFetchRef.current = null;
    await fetchData(true);
  };

  useEffect(() => {
    fetchData();
    return () => { if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current); };
  }, [fetchData]);

  // Group articles by description AND company to avoid duplicates
  const groupArticles = useCallback((articles) => {
    const groups = new Map();
    articles.forEach(article => {
      const key = `${article.description}_${article.company_name}`;
      if (!groups.has(key)) {
        groups.set(key, {
          id: key,
          description: article.description,
          company_name: article.company_name,
          address: article.address,
          articles: [],
          minPrice: Infinity,
          maxPrice: -Infinity,
          totalValue: 0,
          averagePrice: 0,
          count: 0,
        });
      }
      const group = groups.get(key);
      group.articles.push(article);
      group.minPrice = Math.min(group.minPrice, article.unit_price);
      group.maxPrice = Math.max(group.maxPrice, article.unit_price);
      group.totalValue += article.unit_price;
      group.count++;
      group.averagePrice = group.totalValue / group.count;
    });
    return Array.from(groups.values());
  }, []);

  const [groupedData, setGroupedData] = useState([]);
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      let filtered = [...data];
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        filtered = filtered.filter(item =>
          item.company_name?.toLowerCase().includes(s) ||
          item.description?.toLowerCase().includes(s) ||
          item.address?.toLowerCase().includes(s)
        );
      }
      if (minPrice) filtered = filtered.filter(item => item.unit_price >= parseFloat(minPrice));
      if (maxPrice) filtered = filtered.filter(item => item.unit_price <= parseFloat(maxPrice));
      if (selectedCompany) filtered = filtered.filter(item => item.company_name === selectedCompany);
      setGroupedData(groupArticles(filtered));
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [data, searchTerm, minPrice, maxPrice, selectedCompany, groupArticles]);

  const sortedData = useMemo(() => {
    const sorted = [...groupedData];
    sorted.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (['minPrice', 'maxPrice', 'averagePrice', 'count'].includes(sortField)) {
        aVal = aVal || 0;
        bVal = bVal || 0;
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const cmp = String(aVal || '').localeCompare(String(bVal || ''));
      return sortOrder === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [groupedData, sortField, sortOrder]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(start, start + itemsPerPage);
  }, [sortedData, currentPage, itemsPerPage]);

  const handleSort = (field) => {
    if (sortField === field) setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };

  const handleSelectForCompare = (id) => {
    setSelectedForCompare(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSelectAll = () => {
    if (selectedForCompare.length === paginatedData.length) setSelectedForCompare([]);
    else setSelectedForCompare(paginatedData.map(g => g.id));
  };

  const compareGroups = () => {
    if (selectedForCompare.length < 2) {
      setError('Sélectionnez au moins 2 groupes à comparer');
      setTimeout(() => setError(null), 3000);
      return;
    }
    const selected = sortedData.filter(g => selectedForCompare.includes(g.id));
    const summary = {
      totalGroups: selected.length,
      totalArticles: selected.reduce((s, g) => s + g.count, 0),
      minPrice: Math.min(...selected.map(g => g.minPrice)),
      maxPrice: Math.max(...selected.map(g => g.maxPrice)),
      totalValue: selected.reduce((s, g) => s + g.totalValue, 0),
    };
    setComparisonResult({ items: selected, summary });
    setCompareMode(true);
  };

  const exportCSV = () => {
    const headers = ['Description', 'Entreprise', 'Adresse', 'Occurrences', 'Prix min', 'Prix max', 'Prix moyen'];
    const csvData = sortedData.map(g => [g.description, g.company_name, g.address || '', g.count, g.minPrice, g.maxPrice, g.averagePrice]);
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `comparateur_prix_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const resetFilters = () => {
    setSearchTerm('');
    setMinPrice('');
    setMaxPrice('');
    setSelectedCompany('');
  };

  const stats = {
    totalGroups: sortedData.length,
    totalCompanies: companies.length,
    totalValue: sortedData.reduce((s, g) => s + g.totalValue, 0),
    avgPrice: sortedData.length ? sortedData.reduce((s, g) => s + g.averagePrice, 0) / sortedData.length : 0,
  };

  if (loading && !data.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950/50 to-indigo-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-400/20 border-t-blue-400 rounded-full animate-spin" />
          <p className="text-slate-300">Chargement des articles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950/50 to-indigo-950 relative">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="relative max-w-7xl mx-auto p-3 md:p-6 pb-24 md:pb-6">
        {/* Cache info bar */}
        <div className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-2xl p-3 md:p-4 mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 text-xs md:text-sm text-slate-300">
          <div className="flex items-center gap-2 flex-wrap">
            <FiDatabase className="text-blue-400" />
            <span>{cacheDate ? `📦 Cache du ${formatDate(cacheDate)}` : '🔄 Données fraîches'}</span>
            <FiClock className="text-amber-400 ml-2" />
            <span>Cache 24h</span>
          </div>
          <button onClick={clearCacheAndReload} className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-xl text-xs">Vider cache</button>
        </div>

        {/* Desktop Header */}
        <div className="hidden md:block mb-6">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-3xl p-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                  Comparateur de prix d'articles
                </h1>
                <p className="text-slate-400 text-sm mt-1">Articles regroupés par description et entreprise (sans doublon)</p>
              </div>
              <div className="flex gap-3">
                <button onClick={manualRefresh} disabled={isRefreshing} className="flex items-center gap-2 px-4 py-2.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-xl text-sm disabled:opacity-50">
                  <FiRefreshCw className={isRefreshing ? 'animate-spin' : ''} /> {isRefreshing ? 'Mise à jour...' : 'Actualiser'}
                </button>
                <button onClick={exportCSV} className="flex items-center gap-2 px-4 py-2.5 bg-blue-500/10 border border-blue-400/20 text-blue-400 rounded-xl text-sm">
                  <FiDownload /> Exporter
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
              {[
                { label: 'Groupes', value: stats.totalGroups, icon: '📦' },
                { label: 'Entreprises', value: stats.totalCompanies, icon: '🏢' },
                { label: 'Valeur totale', value: formatCurrency(stats.totalValue), icon: '💰' },
                { label: 'Prix moyen', value: `${Math.round(stats.avgPrice).toLocaleString()} CDF`, icon: '⭐' },
              ].map((s, i) => (
                <div key={i} className="bg-white/5 rounded-xl p-3 text-center border border-white/10">
                  <div className="text-2xl mb-1">{s.icon}</div>
                  <div className="text-white font-bold text-lg">{s.value}</div>
                  <div className="text-slate-400 text-xs">{s.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Mobile Header */}
        <div className="md:hidden mb-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-white">Comparateur</h1>
            <p className="text-slate-400 text-xs">{stats.totalGroups} groupes</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setMobileFiltersOpen(true)} className="p-2 rounded-xl bg-white/5 border border-white/10 text-slate-300">
              <FiFilter size={18} />
            </button>
            <button onClick={() => setMobileMenuOpen(true)} className="p-2 rounded-xl bg-white/5 border border-white/10 text-slate-300">
              <FiMenu size={18} />
            </button>
          </div>
        </div>

        {/* Desktop Toolbar */}
        <div className="hidden md:flex flex-wrap items-center gap-4 mb-6">
          <div className="relative flex-1 min-w-[250px]">
            <FiSearch className="absolute left-4 top-3 text-slate-500" />
            <input
              type="text"
              placeholder="Rechercher par description, entreprise..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/5 border border-blue-400/20 rounded-2xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400/20"
            />
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className="px-4 py-3 bg-purple-500/10 border border-purple-500/20 text-purple-400 rounded-xl text-sm flex items-center gap-2">
            <FiFilter /> Filtres
          </button>
          <button onClick={resetFilters} className="px-4 py-3 bg-white/5 border border-white/10 text-slate-300 rounded-xl text-sm flex items-center gap-2">
            <FiRefreshCw /> Réinitialiser
          </button>
          <button
            onClick={compareGroups}
            disabled={selectedForCompare.length < 2}
            className="px-4 py-3 bg-purple-500/10 border border-purple-500/20 text-purple-400 rounded-xl text-sm flex items-center gap-2 disabled:opacity-50"
          >
            <FiTrendingUp /> Comparer ({selectedForCompare.length})
          </button>
        </div>

        {/* Mobile search bar */}
        <div className="md:hidden mb-4">
          <div className="relative">
            <FiSearch className="absolute left-4 top-3 text-slate-500" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/5 border border-blue-400/20 rounded-2xl text-white placeholder-slate-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Filters Panel (desktop) */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden mb-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-white/5 border border-white/10 rounded-2xl">
                <div>
                  <label className="text-slate-400 text-xs uppercase block mb-2">Entreprise</label>
                  <select value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white">
                    <option value="">Toutes</option>
                    {companies.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-slate-400 text-xs uppercase block mb-2">Prix min</label>
                  <input type="number" placeholder="0" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white" />
                </div>
                <div>
                  <label className="text-slate-400 text-xs uppercase block mb-2">Prix max</label>
                  <input type="number" placeholder="1 000 000" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white" />
                </div>
                <div className="flex items-end">
                  <button onClick={resetFilters} className="w-full py-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl font-medium">Réinitialiser</button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-3xl overflow-hidden"
        >
          {loading && !data.length ? (
            <div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-blue-400/20 border-t-blue-400 rounded-full animate-spin" /></div>
          ) : error ? (
            <div className="flex flex-col items-center py-20 text-red-400 gap-4">
              <FiWifiOff size={40} />
              <p>{error}</p>
              <button onClick={manualRefresh} className="px-6 py-2 bg-blue-500/10 border border-blue-400/20 text-blue-400 rounded-xl">Réessayer</button>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10 text-left text-sm text-slate-400">
                      <th className="p-4 w-12">
                        <button onClick={handleSelectAll} className="text-slate-400 hover:text-white">
                          {selectedForCompare.length === paginatedData.length && paginatedData.length > 0 ? <FiCheckSquare size={18} /> : <FiSquare size={18} />}
                        </button>
                      </th>
                      <th className="p-4 cursor-pointer" onClick={() => handleSort('description')}>
                        <div className="flex items-center gap-2">Description {sortField === 'description' && (sortOrder === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />)}</div>
                      </th>
                      <th className="p-4 cursor-pointer" onClick={() => handleSort('company_name')}>
                        <div className="flex items-center gap-2">Entreprise {sortField === 'company_name' && (sortOrder === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />)}</div>
                      </th>
                      <th className="p-4 text-center cursor-pointer" onClick={() => handleSort('count')}>Occ. {sortField === 'count' && (sortOrder === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />)}</th>
                      <th className="p-4 text-right cursor-pointer" onClick={() => handleSort('minPrice')}>Min {sortField === 'minPrice' && (sortOrder === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />)}</th>
                      <th className="p-4 text-right cursor-pointer" onClick={() => handleSort('maxPrice')}>Max {sortField === 'maxPrice' && (sortOrder === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />)}</th>
                      <th className="p-4 text-right cursor-pointer" onClick={() => handleSort('averagePrice')}>Moy. {sortField === 'averagePrice' && (sortOrder === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.length === 0 ? (
                      <tr><td colSpan={7} className="p-8 text-center text-slate-500">Aucun article trouvé</td></tr>
                    ) : (
                      paginatedData.map(group => (
                        <React.Fragment key={group.id}>
                          <tr
                            className="border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                            onClick={() => setExpandedGroups(prev => ({ ...prev, [group.id]: !prev[group.id] }))}
                          >
                            <td className="p-4" onClick={(e) => e.stopPropagation()}>
                              <button onClick={() => handleSelectForCompare(group.id)} className="text-slate-400 hover:text-blue-400">
                                {selectedForCompare.includes(group.id) ? <FiCheckSquare size={18} className="text-blue-400" /> : <FiSquare size={18} />}
                              </button>
                            </td>
                            <td className="p-4 font-medium text-white">
                              <div className="flex items-center gap-2">
                                <motion.span animate={{ rotate: expandedGroups[group.id] ? 90 : 0 }} className="text-blue-400">▶</motion.span>
                                {group.description}
                              </div>
                            </td>
                            <td className="p-4 text-slate-300">{group.company_name}</td>
                            <td className="p-4 text-center"><Badge color="blue">{group.count}</Badge></td>
                            <td className="p-4 text-right text-emerald-400 font-mono">{formatCurrency(group.minPrice)}</td>
                            <td className="p-4 text-right text-red-400 font-mono">{formatCurrency(group.maxPrice)}</td>
                            <td className="p-4 text-right text-blue-400 font-mono">{formatCurrency(group.averagePrice)}</td>
                          </tr>
                          <AnimatePresence>
                            {expandedGroups[group.id] && group.articles.map((article, idx) => (
                              <motion.tr
                                key={idx}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="border-b border-white/5 bg-white/[0.02] hover:bg-white/[0.04] cursor-pointer"
                                onClick={() => { setSelectedItem(article); setShowModal(true); }}
                              >
                                <td className="p-4"></td>
                                <td className="p-4 pl-10 text-slate-300 text-sm">└ {article.description}</td>
                                <td className="p-4 text-slate-400 text-sm">{article.company_name}</td>
                                <td className="p-4"></td>
                                <td className="p-4"></td>
                                <td className="p-4"></td>
                                <td className="p-4 text-right text-emerald-300 font-mono text-sm">{formatCurrency(article.unit_price)}</td>
                              </motion.tr>
                            ))}
                          </AnimatePresence>
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden p-4 space-y-3">
                {paginatedData.length === 0 ? (
                  <div className="text-center py-10 text-slate-400">Aucun article</div>
                ) : (
                  paginatedData.map(group => (
                    <ArticleCardMobile
                      key={group.id}
                      group={group}
                      expanded={!!expandedGroups[group.id]}
                      onToggle={(id) => setExpandedGroups(prev => ({ ...prev, [id]: !prev[id] }))}
                      onSelect={handleSelectForCompare}
                      selected={selectedForCompare.includes(group.id)}
                      onViewDetails={(item) => { setSelectedItem(item); setShowModal(true); }}
                    />
                  ))
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 border-t border-white/10">
                  <div className="text-sm text-slate-400">
                    {((currentPage - 1) * itemsPerPage) + 1}–{Math.min(currentPage * itemsPerPage, sortedData.length)} sur {sortedData.length}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30"><FiChevronsLeft size={16} /></button>
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30"><FiChevronLeft size={16} /></button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                      else pageNum = currentPage - 2 + i;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`w-8 h-8 rounded-lg text-sm font-medium ${
                            currentPage === pageNum ? 'bg-blue-500/20 text-blue-400 border border-blue-400/30' : 'text-slate-400 hover:bg-white/10'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30"><FiChevronRight size={16} /></button>
                    <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30"><FiChevronsRight size={16} /></button>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <span>Afficher</span>
                    <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-white">
                      <option value={5}>5</option><option value={10}>10</option><option value={25}>25</option><option value={50}>50</option>
                    </select>
                  </div>
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {showModal && selectedItem && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
            <motion.div initial={{ scale: 0.9, y: 50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 50 }} onClick={(e) => e.stopPropagation()} className="bg-gradient-to-b from-blue-950/95 to-slate-900/95 backdrop-blur-xl border border-blue-400/20 rounded-3xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white">Détail article</h2>
                <button onClick={() => setShowModal(false)} className="p-2 rounded-xl bg-white/5 text-slate-400"><FiX size={20} /></button>
              </div>
              <div className="space-y-3 text-sm">
                <div><span className="text-slate-500">ID</span><p className="text-white font-mono">#{selectedItem.id}</p></div>
                <div><span className="text-slate-500">Description</span><p className="text-white">{selectedItem.description}</p></div>
                <div><span className="text-slate-500">Entreprise</span><p className="text-white">{selectedItem.company_name}</p></div>
                <div><span className="text-slate-500">Adresse</span><p className="text-white">{selectedItem.address || '—'}</p></div>
                <div><span className="text-slate-500">Prix unitaire</span><p className="text-emerald-400 font-bold text-lg">{formatCurrency(selectedItem.unit_price)}</p></div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Comparison Modal */}
      <AnimatePresence>
        {compareMode && comparisonResult && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4" onClick={() => setCompareMode(false)}>
            <motion.div initial={{ scale: 0.9, y: 50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 50 }} onClick={(e) => e.stopPropagation()} className="bg-gradient-to-b from-blue-950/95 to-slate-900/95 backdrop-blur-xl border border-blue-400/20 rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Comparaison de prix</h2>
                  <button onClick={() => setCompareMode(false)} className="p-2 rounded-xl bg-white/5 text-slate-400"><FiX size={20} /></button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {[
                    { label: 'Groupes', value: comparisonResult.summary.totalGroups },
                    { label: 'Articles', value: comparisonResult.summary.totalArticles },
                    { label: 'Prix min global', value: formatCurrency(comparisonResult.summary.minPrice), color: 'text-emerald-400' },
                    { label: 'Prix max global', value: formatCurrency(comparisonResult.summary.maxPrice), color: 'text-red-400' },
                  ].map((s, i) => (
                    <div key={i} className="bg-white/5 rounded-xl p-4 text-center">
                      <div className="text-slate-400 text-xs mb-1">{s.label}</div>
                      <div className={`text-white font-bold text-xl ${s.color || ''}`}>{s.value}</div>
                    </div>
                  ))}
                </div>

                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-left text-slate-400">
                      <th className="p-3">Article</th>
                      <th className="p-3">Entreprise</th>
                      <th className="p-3 text-center">Occ.</th>
                      <th className="p-3 text-right">Min</th>
                      <th className="p-3 text-right">Max</th>
                      <th className="p-3 text-right">Moy.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonResult.items.map((item, idx) => (
                      <tr key={idx} className="border-b border-white/5">
                        <td className="p-3 text-white font-medium">{item.description}</td>
                        <td className="p-3 text-slate-300">{item.company_name}</td>
                        <td className="p-3 text-center"><Badge color="blue">{item.count}</Badge></td>
                        <td className="p-3 text-right text-emerald-400 font-mono">{formatCurrency(item.minPrice)}</td>
                        <td className="p-3 text-right text-red-400 font-mono">{formatCurrency(item.maxPrice)}</td>
                        <td className="p-3 text-right text-blue-400 font-mono">{formatCurrency(item.averagePrice)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notifications */}
      <AnimatePresence>
        {success && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="fixed top-4 right-4 z-50 px-5 py-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-2xl backdrop-blur-xl text-sm">
            {success}
          </motion.div>
        )}
        {error && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="fixed top-4 right-4 z-50 px-5 py-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-2xl backdrop-blur-xl text-sm">
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menus */}
      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        onRefresh={manualRefresh}
        onClearCache={clearCacheAndReload}
        onExport={exportCSV}
        onCompare={compareGroups}
        compareCount={selectedForCompare.length}
        isRefreshing={isRefreshing}
      />
      <MobileFilters
        isOpen={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        companies={companies}
        selectedCompany={selectedCompany}
        setSelectedCompany={setSelectedCompany}
        minPrice={minPrice}
        setMinPrice={setMinPrice}
        maxPrice={maxPrice}
        setMaxPrice={setMaxPrice}
        onReset={resetFilters}
      />

      {/* Connection status */}
      <div className={`fixed bottom-4 right-4 z-50 px-4 py-2 rounded-full text-xs font-medium flex items-center gap-2 backdrop-blur-xl border ${
        isOnline ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'
      }`}>
        <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
        <span>{isOnline ? 'En ligne' : 'Hors ligne'}</span>
      </div>
    </div>
  );
};

export default ActiclesTable;